import { FetchResult } from 'src/types/TApiQueries';
import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from 'src/api/utils/helpers';
import { SignupCampaign } from 'src/utils/campaign';

// Mirrors filespin/api/signup_public.py DemoSignupValidateHandler.
export type DemoSignupValidateRequest = {
  demo_run_id: string;
  claim_token: string;
};

export type DemoSignupValidateResponse = {
  email: string;
  suggested_first_name: string;
  industry: string;
  run_id: string;
  account_exists: boolean;
};

export type DemoSignupCompleteRequest = {
  demo_run_id: string;
  claim_token: string;
  password: string;
  first_name?: string;
  last_name?: string;
};

export type DemoSignupAttachRequest = {
  demo_run_id: string;
  claim_token: string;
  password: string;
};

// The /complete and /attach endpoints both return the standard login envelope
// shared with /api/v1/login + UserActivationHandler, plus a demo_claim block.
export type DemoSignupLoginResponse = {
  jwt: string;
  user_id: number;
  user_email: string;
  username?: string;
  role_id: number;
  role_name: string;
  permissions: string[];
  uploadkey?: string;
  accessID?: string;
  preferences?: any;
  notifications?: any[];
  org_name?: string;
  industry?: string;
  // Coarse capability flag set server-side from `ai_settings.enabled` for
  // the new account's group. Lets the UI seed `addonsSettingsAtom` and the
  // `useAiAgentAvailable` hook optimistically so the dashboard doesn't
  // flicker "AI Assistant not available" between page load and the
  // /api/v1/settings/ai round-trip on first sign-in.
  ai_assistant_available?: boolean;
  demo_claim?: {
    status: 'OK' | 'ALREADY_CLAIMED' | 'INVALID_TOKEN' | 'ERROR';
    share_id?: string;
    share_url?: string;
    asset_count?: number;
    output_name?: string;
  };
};

const _post = <T>(path: string, body: any): Promise<FetchResult<T>> => {
  return performFetchWithLogging<T>(`${config.hostUrl}${path}`, {
    method: 'POST',
    headers: getStandardHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });
};

export function validateDemoSignup(
  req: DemoSignupValidateRequest,
): Promise<FetchResult<DemoSignupValidateResponse>> {
  return _post<DemoSignupValidateResponse>('/api/v1/signup/demo/validate', req);
}

export function completeDemoSignup(
  req: DemoSignupCompleteRequest,
): Promise<FetchResult<DemoSignupLoginResponse>> {
  return _post<DemoSignupLoginResponse>('/api/v1/signup/demo/complete', req);
}

export function attachDemoToAccount(
  req: DemoSignupAttachRequest,
): Promise<FetchResult<DemoSignupLoginResponse>> {
  return _post<DemoSignupLoginResponse>('/api/v1/signup/demo/attach', req);
}

// ---- General signup page (/signup) --------------------------------------

export type SignupOption = { value: string; label: string };

// GET /api/v1/signup/options - industry picklist for the signup forms.
export type SignupOptionsResponse = {
  industries: SignupOption[];
  default_industry: string;
};

export function fetchSignupOptions(): Promise<FetchResult<SignupOptionsResponse>> {
  return performFetchWithLogging<SignupOptionsResponse>(`${config.hostUrl}/api/v1/signup/options`, {
    method: 'GET',
    headers: getStandardHeaders(),
  });
}

// POST /api/v1/signup - email signup. Always 202 with a generic
// "check your email" payload, whatever the ICP gate decided.
export type PublicSignupRequest = {
  email: string;
  first_name?: string;
  last_name?: string;
  industry?: string;
  source?: string;
  // utm_* labels from the signup link and the page URL (src/utils/campaign.ts).
  campaign?: SignupCampaign;
  page?: string;
};

export type PublicSignupResponse = { status: string; message: string };

export function publicSignup(req: PublicSignupRequest): Promise<FetchResult<PublicSignupResponse>> {
  return _post<PublicSignupResponse>('/api/v1/signup', req);
}
